@if (windowService.isDesktopRes) {
  <tidy-desktop-header>
    <div class="wrappable-container monthly-toolbar-desktop">
      <tidy-text [title]="'Monthly P&amp;L'" />

      <div class="vertical-align-center year-nav-pill">
        <tidy-image
          (click)="prevYear()"
          [src]="'assets/icon/arrowhead.png'"
          class="body-size arrow-icon reverse-arrow" />
        <tidy-text [body]="year().toString()" />
        <tidy-image
          (click)="nextYear()"
          [src]="'assets/icon/arrowhead.png'"
          class="body-size arrow-icon last-arrow" />
      </div>

      <div class="layer-switch" role="tablist" aria-label="P&L layer">
        <button
          class="layer-option"
          [class.active]="layer() === 'actual'"
          (click)="setLayer('actual')">
          <tidy-text [headerButton]="'Actuals'" class="white" />
        </button>
        <button
          class="layer-option"
          [class.active]="layer() === 'projected'"
          (click)="setLayer('projected')">
          <tidy-text [headerButton]="'Projected'" class="white" />
        </button>
      </div>

      <tidy-month-attribution-switch
        [value]="attribution()"
        [disabledReason]="attributionDisabledReason()"
        (valueChange)="setAttribution($event)" />

      <tidy-header-select
        [items]="propertyHeaderItems()"
        [icon]="'assets/svg/home-outline.svg'"
        [selectedValue]="selectedProperty()"
        (optionChange)="setProperty($event)" />

      <tidy-small-button
        [text]="'History'"
        [image]="'assets/svg/time-outline.svg'"
        (buttonClick)="openHistory()" />

      @if (canImportProjections) {
        <tidy-small-button
          [iconOnly]="true"
          [image]="'assets/svg/cloud-upload-outline.svg'"
          (buttonClick)="openUploadProjections()" />
      }
    </div>
    <div class="non-wrappable-right-aligned-container">
      @if (showRefreshSpinner) {
        <ion-spinner name="lines" class="header-loading-spinner" style="margin-right: 15px" />
      }
      <automations-preview-button
        [tag]="'monthly-pl'" />
    </div>
  </tidy-desktop-header>
}

<div class="monthly-content" [class.layer-projected]="layer() === 'projected'" [class.layer-actual]="layer() === 'actual'">
  @if (!windowService.isDesktopRes) {
    <div class="monthly-toolbar-mobile">
      <!-- Row 1: year nav (left) + actuals/projected and accrual/cash toggles (right) -->
      <div class="monthly-toolbar-mobile__row">
        <div class="vertical-align-center year-nav-pill">
          <tidy-image
            (click)="prevYear()"
            [src]="'assets/icon/arrowhead.png'"
            class="body-size arrow-icon reverse-arrow" />
          <tidy-text [body]="year().toString()" />
          <tidy-image
            (click)="nextYear()"
            [src]="'assets/icon/arrowhead.png'"
            class="body-size arrow-icon last-arrow" />
        </div>
        <div class="monthly-toolbar-mobile__switches">
          <div class="layer-switch" role="tablist" aria-label="P&L layer">
            <button
              class="layer-option"
              [class.active]="layer() === 'actual'"
              (click)="setLayer('actual')">
              <tidy-text [headerButton]="'Actuals'" class="white" />
            </button>
            <button
              class="layer-option"
              [class.active]="layer() === 'projected'"
              (click)="setLayer('projected')">
              <tidy-text [headerButton]="'Projected'" class="white" />
            </button>
          </div>
          <tidy-month-attribution-switch
            [value]="attribution()"
            [disabledReason]="attributionDisabledReason()"
            (valueChange)="setAttribution($event)" />
        </div>
      </div>

      <!-- Row 2: property header-select (left) + history small-button (right) -->
      <div class="monthly-toolbar-mobile__row">
        <tidy-header-select
          [items]="propertyHeaderItems()"
          [icon]="'assets/svg/home-outline.svg'"
          [selectedValue]="selectedProperty()"
          (optionChange)="setProperty($event)" />
        <tidy-small-button
          [text]="'History'"
          [image]="'assets/svg/time-outline.svg'"
          (buttonClick)="openHistory()" />
        @if (canImportProjections) {
          <tidy-small-button
            [iconOnly]="true"
            [image]="'assets/svg/cloud-upload-outline.svg'"
            (buttonClick)="openUploadProjections()" />
        }
      </div>
    </div>
  }

  @if (isLoaded) {
  <div class="monthly-table-wrap">
    <table class="monthly-grid">
      <thead>
        <tr>
          <th class="col-label sticky-col">Line item</th>
          @for (m of months(); track m.label) {
            <th
              class="col-month"
              [class.is-now]="m.isNow"
              [class.disabled]="m.disabled">
              {{ m.label }}
            </th>
          }
          <th class="col-total">{{ year() }} Total</th>
        </tr>
      </thead>

      <tbody>
        <tr class="section-row">
          <th class="sticky-col" [attr.colspan]="14">
            <tidy-text [helper]="'Income'" />
          </th>
        </tr>

        @for (group of grid().income; track group.id) {
          <tr
            class="group-row"
            [class.contra]="group.kind === 'contra-revenue'"
            (click)="toggleGroup(group.id)">
            <td class="col-label sticky-col">
              <span class="chevron" [class.open]="group.expanded">›</span>
              <tidy-text [body]="group.label" class="group-label bold" />
              @if (group.kind === 'contra-revenue') {
                <span class="contra-flag">subtracts</span>
              }
            </td>
            @for (cell of group.cells; track $index; let i = $index) {
              <td
                class="col-month"
                [class.opens-transactions]="grid().opensTransactions && !cell.disabled"
                [class.disabled]="cell.disabled"
                (click)="$event.stopPropagation(); openGroupTransactions(group, i)">
                {{ cell.value }}
              </td>
            }
            <td class="col-total">{{ group.total }}</td>
          </tr>

          @if (group.expanded) {
            @for (row of group.lines; track row.key) {
              <tr class="data-row sub-row">
                <td class="col-label sticky-col">
                  <span class="row-indent"></span>
                  <tidy-text [body]="row.label" class="sub-label" />
                </td>
                @for (cell of row.cells; track $index; let i = $index) {
                  <td
                    class="col-month cell"
                    [class.is-now]="cell.isNow"
                    [class.manual]="cell.manual"
                    [class.disabled]="cell.disabled"
                    [class.opens-transactions]="grid().opensTransactions && grid().viewingAll && !cell.disabled"
                    (click)="onCellClick($event, row, i)"
                    (dblclick)="openCellTransactions(row, i)">
                    @if (cell.disabled) {
                      <span class="cell-na">—</span>
                    } @else if (grid().viewingAll) {
                      <span class="cell-aggregate">{{ cell.aggregate }}</span>
                    } @else {
                      <input
                        class="cell-input"
                        type="text"
                        inputmode="numeric"
                        placeholder="—"
                        [value]="cell.display"
                        (input)="onCellInput(row.key, i, row.sign, $any($event.target).value)"
                        (change)="onCellCommit(row.key, i)" />
                      @if (cell.manual) {
                        <button
                          class="revert-dot"
                          title="Clear"
                          (click)="revertCell(row.key, i, $event)">↺</button>
                      }
                    }
                  </td>
                }
                <td class="col-total">{{ row.total }}</td>
              </tr>
            }
          }
        }

        <tr class="subtotal-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Total net income'" class="bold" />
          </td>
          @for (cell of grid().incomeTotals; track $index; let i = $index) {
            <td
              class="col-month"
              [class.opens-transactions]="grid().opensTransactions && !cell.disabled"
              [class.disabled]="cell.disabled"
              (click)="openTotalTransactions('income', i)">
              {{ cell.value }}
            </td>
          }
          <td class="col-total">{{ grid().incomeYear }}</td>
        </tr>

        <tr class="section-row">
          <th class="sticky-col" [attr.colspan]="14">
            <tidy-text [helper]="'Operating expenses'" />
          </th>
        </tr>

        @for (group of grid().expenses; track group.id) {
          <tr
            class="group-row"
            (click)="toggleGroup(group.id)">
            <td class="col-label sticky-col">
              <span class="chevron" [class.open]="group.expanded">›</span>
              <tidy-text [body]="group.label" class="group-label bold" />
            </td>
            @for (cell of group.cells; track $index; let i = $index) {
              <td
                class="col-month"
                [class.opens-transactions]="grid().opensTransactions && !cell.disabled"
                [class.disabled]="cell.disabled"
                (click)="$event.stopPropagation(); openGroupTransactions(group, i)">
                {{ cell.value }}
              </td>
            }
            <td class="col-total">{{ group.total }}</td>
          </tr>

          @if (group.expanded) {
            @for (row of group.lines; track row.key) {
              <tr class="data-row sub-row">
                <td class="col-label sticky-col">
                  <span class="row-indent"></span>
                  <tidy-text [body]="row.label" class="sub-label" />
                </td>
                @for (cell of row.cells; track $index; let i = $index) {
                  <td
                    class="col-month cell"
                    [class.is-now]="cell.isNow"
                    [class.manual]="cell.manual"
                    [class.disabled]="cell.disabled"
                    [class.opens-transactions]="grid().opensTransactions && grid().viewingAll && !cell.disabled"
                    (click)="onCellClick($event, row, i)"
                    (dblclick)="openCellTransactions(row, i)">
                    @if (cell.disabled) {
                      <span class="cell-na">—</span>
                    } @else if (grid().viewingAll) {
                      <span class="cell-aggregate">{{ cell.aggregate }}</span>
                    } @else {
                      <input
                        class="cell-input"
                        type="text"
                        inputmode="numeric"
                        placeholder="—"
                        [value]="cell.display"
                        (input)="onCellInput(row.key, i, row.sign, $any($event.target).value)"
                        (change)="onCellCommit(row.key, i)" />
                      @if (cell.manual) {
                        <button
                          class="revert-dot"
                          title="Clear"
                          (click)="revertCell(row.key, i, $event)">↺</button>
                      }
                    }
                  </td>
                }
                <td class="col-total">{{ row.total }}</td>
              </tr>
            }
          }
        }

        <tr class="subtotal-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Total operating expenses'" class="bold" />
          </td>
          @for (cell of grid().expenseTotals; track $index; let i = $index) {
            <td
              class="col-month"
              [class.opens-transactions]="grid().opensTransactions && !cell.disabled"
              [class.disabled]="cell.disabled"
              (click)="openTotalTransactions('expenses', i)">
              {{ cell.value }}
            </td>
          }
          <td class="col-total">{{ grid().expensesYear }}</td>
        </tr>

        <tr class="net-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Net operating income (NOI)'" class="bold" />
          </td>
          @for (cell of grid().noi; track $index; let i = $index) {
            <td
              class="col-month"
              [class.opens-transactions]="grid().opensTransactions && !cell.disabled"
              [class.disabled]="cell.disabled"
              (click)="openTotalTransactions('noi', i)">
              {{ cell.value }}
            </td>
          }
          <td class="col-total">{{ grid().noiYear }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="monthly-footnote">
    @if (isViewingAll()) {
      Showing <strong>all properties</strong> — values are the sum across every property.
      Pick a specific property to edit entries.
      @if (layer() === 'actual') {
        Tap a number to see the transactions behind it.
      }
    } @else if (layer() === 'actual') {
      Showing <strong>actuals</strong> for {{ currentPropertyLabel() }} — only what you've
      entered. Future months have no actuals.
      Double-tap a number to see the transactions behind it.
    } @else {
      Showing <strong>projections</strong> for {{ currentPropertyLabel() }} — your forecast
      for every month.
    }
  </div>
  } @else {
  <!-- First-load skeleton. Mirrors the loaded grid exactly — same wrapper,
       table, row and cell classes — so the swap to real data happens in
       place with zero shift. Static chrome (month headers, section labels,
       subtotal/NOI labels) renders as real text; only data-driven cells are
       grey stubs. The wrap floats in, then rows ripple top-down. Skeleton
       DOM only exists before the first load resolves, so route-reuse
       reattachment can never replay the entrance. -->
  <div class="monthly-table-wrap sk-table-wrap" aria-hidden="true">
    <table class="monthly-grid">
      <thead>
        <tr>
          <th class="col-label sticky-col">Line item</th>
          @for (m of months(); track m.label) {
            <th
              class="col-month"
              [class.is-now]="m.isNow"
              [class.disabled]="m.disabled">
              {{ m.label }}
            </th>
          }
          <th class="col-total">{{ year() }} Total</th>
        </tr>
      </thead>

      <tbody>
        <tr class="section-row">
          <th class="sticky-col" [attr.colspan]="14">
            <tidy-text [helper]="'Income'" />
          </th>
        </tr>

        @for (g of [1, 2, 3, 4]; track g) {
          <tr class="group-row">
            <td class="col-label sticky-col">
              <span class="chevron">›</span>
              <ion-skeleton-text [animated]="true" class="sk-label" />
            </td>
            @for (m of months(); track m.label) {
              <td class="col-month" [class.disabled]="m.disabled">
                @if (m.disabled) {
                  —
                } @else {
                  <ion-skeleton-text [animated]="true" class="sk-num" />
                }
              </td>
            }
            <td class="col-total"><ion-skeleton-text [animated]="true" class="sk-total" /></td>
          </tr>
        }

        <tr class="subtotal-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Total net income'" class="bold" />
          </td>
          @for (m of months(); track m.label) {
            <td class="col-month" [class.disabled]="m.disabled">
              @if (m.disabled) {
                —
              } @else {
                <ion-skeleton-text [animated]="true" class="sk-num" />
              }
            </td>
          }
          <td class="col-total"><ion-skeleton-text [animated]="true" class="sk-total" /></td>
        </tr>

        <tr class="section-row">
          <th class="sticky-col" [attr.colspan]="14">
            <tidy-text [helper]="'Operating expenses'" />
          </th>
        </tr>

        @for (g of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; track g) {
          <tr class="group-row">
            <td class="col-label sticky-col">
              <span class="chevron">›</span>
              <ion-skeleton-text [animated]="true" class="sk-label" />
            </td>
            @for (m of months(); track m.label) {
              <td class="col-month" [class.disabled]="m.disabled">
                @if (m.disabled) {
                  —
                } @else {
                  <ion-skeleton-text [animated]="true" class="sk-num" />
                }
              </td>
            }
            <td class="col-total"><ion-skeleton-text [animated]="true" class="sk-total" /></td>
          </tr>
        }

        <tr class="subtotal-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Total operating expenses'" class="bold" />
          </td>
          @for (m of months(); track m.label) {
            <td class="col-month" [class.disabled]="m.disabled">
              @if (m.disabled) {
                —
              } @else {
                <ion-skeleton-text [animated]="true" class="sk-num" />
              }
            </td>
          }
          <td class="col-total"><ion-skeleton-text [animated]="true" class="sk-total" /></td>
        </tr>

        <tr class="net-row">
          <td class="col-label sticky-col">
            <tidy-text [body]="'Net operating income (NOI)'" class="bold" />
          </td>
          @for (m of months(); track m.label) {
            <td class="col-month" [class.disabled]="m.disabled">
              @if (m.disabled) {
                —
              } @else {
                <ion-skeleton-text [animated]="true" class="sk-num" />
              }
            </td>
          }
          <td class="col-total"><ion-skeleton-text [animated]="true" class="sk-total" /></td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="monthly-footnote sk-footnote-row" aria-hidden="true">
    <ion-skeleton-text [animated]="true" class="sk-footnote" />
  </div>
  }
</div>

@if (showAboutThisPage()) {
  <tidy-about-this-page [page]="'monthly-pl'" (dismissed)="dismissAboutThisPage()" />
}
